// PagerDuty Utility Functions
//
// This module contains utility functions for working with PagerDuty integrations.

/**
 * Generates a PagerDuty workflow URL for editing/viewing workflows
 * @param {string} workflowId - The PagerDuty workflow ID
 * @param {Object} configuration - Configuration object containing PagerDuty settings
 * @param {Object} configuration.pagerduty - PagerDuty-specific configuration
 * @param {string} configuration.pagerduty.hostname - PagerDuty hostname (e.g., 'coxauto', 'coxautonp')
 * @returns {string|null} The complete PagerDuty workflow URL or null if invalid workflow ID
 */
export const generatePagerDutyWorkflowURL = (
  workflowId,
  configuration = {}
) => {
  // Return null for invalid or empty workflow IDs
  if (
    !workflowId ||
    workflowId === "" ||
    (typeof workflowId === "string" && workflowId.toLowerCase() === "none")
  ) {
    return null;
  }

  // Use configured hostname or default to production hostname
  const hostname = configuration?.pagerduty?.hostname || "coxauto";

  // Construct the PagerDuty workflow edit URL
  return `https://${hostname}.pagerduty.com/incident-workflows/workflows/${workflowId}/edit`;
};

/**
 * Generates a PagerDuty incident timeline URL
 * @param {string} incidentId - The PagerDuty incident ID
 * @param {Object} configuration - Configuration object containing PagerDuty settings
 * @returns {string|null} The complete PagerDuty incident timeline URL or null if invalid incident ID
 */
export const generatePagerDutyIncidentURL = (
  incidentId,
  configuration = {}
) => {
  if (!incidentId || incidentId === "") {
    return null;
  }

  const hostname = configuration?.pagerduty?.hostname || "coxauto";
  return `https://${hostname}.pagerduty.com/incidents/${incidentId}/timeline`;
};

/**
 * Validates if a workflow ID is usable (not empty, null, or "none")
 * @param {string} workflowId - The workflow ID to validate
 * @returns {boolean} True if the workflow ID is valid and usable
 */
export const isValidWorkflowId = (workflowId) => {
  return (
    Boolean(workflowId) &&
    typeof workflowId === "string" &&
    workflowId.trim() !== "" &&
    workflowId.toLowerCase() !== "none"
  );
};
